import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import {
  RebateOffer,
  RebateOfferState,
  RebateOfferType,
} from './tradespend_api'
import { formatInteger, formatUsd } from 'utils/formatting'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { REBATE_OFFERS_COLUMNS } from './rebateOffersColumnInfo'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { RebateOfferTypePill } from './RebateOfferTypePill'
import { RebateOfferStatePill } from './RebateOfferStatePill'
import { FbLink } from 'components/FbUI/FbLink'
import { OverflownScrollList } from 'components/Tables/CommonTableCells/OverflownScrollList'
import { DescriptionCell } from 'components/Tables/CommonTableCells/DescriptionCell'
import { PiCheck, PiGlobe } from 'react-icons/pi'

export function useRebateOffersColumns() {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const columnHelper = createColumnHelper<RebateOffer>()
  const offerColumnHelper = createTableColumnHelper({
    columns: REBATE_OFFERS_COLUMNS,
    tooltips,
  })

  return [
    columnHelper.accessor('name', {
      ...offerColumnHelper.retrieveColumn('name'),
      size: 200,
      cell: (info) => (
        <div
          className="break-words whitespace-normal max-w-full overflow-hidden text-ellipsis"
          title={info.getValue()}
        >
          <FbLink to={`/rebates/offer/${info.row.original.id}/details`}>
            {info.getValue()}
          </FbLink>
        </div>
      ),
    }),
    columnHelper.accessor('description', {
      ...offerColumnHelper.retrieveColumn('description'),
      size: 500,
      cell: (info) => <DescriptionCell value={info.getValue()} />,
    }),
    columnHelper.accessor('learn_more_url', {
      ...offerColumnHelper.retrieveColumn('learn_more_url'),
      size: 200,
      cell: (info) => {
        const url = info.getValue() as string | null
        if (!url) return '-'
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Learn more"
            title="Learn more"
            // Don't let the link also fire the row's navigate-to-offer click.
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center text-brand-bright hover:text-brand-active"
          >
            <PiGlobe size={20} />
          </a>
        )
      },
    }),
    columnHelper.accessor('state', {
      ...offerColumnHelper.retrieveColumn('state'),
      size: 120,
      cell: (info) => (
        <RebateOfferStatePill state={info.getValue() as RebateOfferState} />
      ),
    }),
    columnHelper.accessor('scope', {
      ...offerColumnHelper.retrieveColumn('scope'),
      size: 120,
      cell: (info) => {
        const value = info.getValue() as string | null
        if (!value) return '-'
        const label =
          value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
        return (
          <span
            className="inline-flex items-center rounded-2xl bg-neutral-100 px-2.5 py-0.5 text-sm font-medium leading-5 text-neutral-700"
            style={{ letterSpacing: '-0.2px' }}
          >
            {label}
          </span>
        )
      },
    }),
    columnHelper.accessor('is_shared_with_network', {
      ...offerColumnHelper.retrieveColumn('is_shared_with_network'),
      size: 120,
      cell: (info) =>
        info.getValue() ? (
          <span
            className="inline-flex items-center gap-1 rounded-2xl bg-green-100 px-2.5 py-0.5 text-sm font-medium leading-5 text-green-700"
            style={{ letterSpacing: '-0.2px' }}
          >
            <PiCheck size={14} />
            Yes
          </span>
        ) : (
          '-'
        ),
    }),
    columnHelper.accessor('offer_type', {
      ...offerColumnHelper.retrieveColumn('offer_type'),
      size: 150,
      cell: (info) => (
        <RebateOfferTypePill type={info.getValue() as RebateOfferType} />
      ),
    }),
    columnHelper.display({
      id: 'total_views',
      header: 'Total Views',
      size: 120,
      cell: (info) => {
        const attributionCodes = info.row.original.attribution_codes || []
        const totalViews = attributionCodes.reduce(
          (sum, code) => sum + (code.views || 0),
          0
        )
        return formatInteger(totalViews)
      },
    }),
    columnHelper.display({
      id: 'total_emails_captured',
      header: 'Total Emails Captured',
      size: 180,
      cell: (info) => {
        const attributionCodes = info.row.original.attribution_codes || []
        const totalEmailsCaptured = attributionCodes.reduce(
          (sum, code) => sum + (code.emails_captured || 0),
          0
        )
        return formatInteger(totalEmailsCaptured)
      },
    }),
    columnHelper.display({
      id: 'total_clicks',
      header: 'Total Clicks',
      size: 120,
      cell: (info) => {
        const attributionCodes = info.row.original.attribution_codes || []
        const totalClicks = attributionCodes.reduce(
          (sum, code) => sum + (code.visits || 0),
          0
        )
        return formatInteger(totalClicks)
      },
    }),
    columnHelper.accessor('total_enrollments', {
      ...offerColumnHelper.retrieveColumn('total_enrollments'),
      size: 120,
      cell: (info) => formatInteger(info.getValue() || 0),
    }),
    columnHelper.accessor('total_claims', {
      ...offerColumnHelper.retrieveColumn('total_claims'),
      size: 120,
      cell: (info) => formatInteger(info.getValue() || 0),
    }),
    columnHelper.accessor('pending_validation_claims', {
      ...offerColumnHelper.retrieveColumn('pending_validation_claims'),
      size: 120,
      cell: (info) => formatInteger(info.getValue() || 0),
    }),
    columnHelper.accessor('invalid_claims', {
      ...offerColumnHelper.retrieveColumn('invalid_claims'),
      size: 120,
      cell: (info) => formatInteger(info.getValue() || 0),
    }),
    columnHelper.accessor('paid_claims', {
      ...offerColumnHelper.retrieveColumn('paid_claims'),
      size: 120,
      cell: (info) => formatInteger(info.getValue() || 0),
    }),
    columnHelper.accessor('unpaid_valid_claims', {
      ...offerColumnHelper.retrieveColumn('unpaid_valid_claims'),
      size: 120,
      cell: (info) => formatInteger(info.getValue() || 0),
    }),
    columnHelper.accessor('total_validated_units', {
      ...offerColumnHelper.retrieveColumn('total_validated_units'),
      size: 120,
      cell: (info) => formatInteger(info.getValue() || 0),
    }),
    columnHelper.accessor('total_validated_payout', {
      ...offerColumnHelper.retrieveColumn('total_validated_payout'),
      size: 150,
      cell: (info) => formatUsd(info.getValue() || 0),
    }),
    columnHelper.accessor('valid_from', {
      ...offerColumnHelper.retrieveColumn('valid_from'),
      size: 150,
      cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
    }),
    columnHelper.accessor('valid_to', {
      ...offerColumnHelper.retrieveColumn('valid_to'),
      size: 150,
      cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
    }),
    columnHelper.accessor('product_names', {
      ...offerColumnHelper.retrieveColumn('product_names'),
      size: 200,
      cell: (info) => {
        const value = info.getValue()
        const items = value ? value.split(', ').filter(Boolean) : []
        if (items.length === 0) return '-'
        return <OverflownScrollList items={items} />
      },
    }),
    columnHelper.accessor('modified', {
      ...offerColumnHelper.retrieveColumn('modified'),
      size: 200,
      cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
    }),
    columnHelper.accessor('created', {
      ...offerColumnHelper.retrieveColumn('created'),
      size: 200,
      cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy'),
    }),
  ]
}
